import React, { useState, useCallback } from "react";
import { Form, Input, Button, DatePicker, Upload, notification, Card, Row, Col, Typography, Result, Modal, Slider, Select } from "antd";
import { UploadOutlined, UserOutlined, ScissorOutlined } from "@ant-design/icons";
import Cropper from "react-easy-crop";
import axios from "axios";
import { baseUrl } from "../config";

const { Title, Text } = Typography;

// ── Validation helpers ─────────────────────────────────────────────────────
const DEVA_REGEX = /^[\u0900-\u097F\s.,'\-\u200B\u200C\u200D]+$/;
const URL_REGEX = /(https?:\/\/|www\.|\.com|\.net|\.org|\.in|<[^>]+>|javascript:|on\w+=|&lt;|&#)/i;

const validateHindi = (_, value) => {
  if (!value || value.trim() === '') return Promise.resolve();
  if (URL_REGEX.test(value)) return Promise.reject('लिंक या स्क्रिप्ट की अनुमति नहीं है');
  if (!DEVA_REGEX.test(value.trim())) return Promise.reject('कृपया हिंदी (देवनागरी) में लिखें');
  return Promise.resolve();
};

const validateNoLinks = (_, value) => {
  if (!value || value.trim() === '') return Promise.resolve();
  if (URL_REGEX.test(value)) return Promise.reject('Links या Scripts की अनुमति नहीं है');
  return Promise.resolve();
};

// ── Canvas crop helper ─────────────────────────────────────────────────────
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (err) => reject(err));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const maxDim = 800;
  const scale = Math.min(maxDim / pixelCrop.width, maxDim / pixelCrop.height, 1);
  canvas.width = Math.round(pixelCrop.width * scale);
  canvas.height = Math.round(pixelCrop.height * scale);
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.82);
  });
}

// ── Component ──────────────────────────────────────────────────────────────
// drawerMode: true  → renders without full-page wrapper, closes on success
// drawerMode: false → renders as standalone public page (default)
function BirthdayForm({ drawerMode = false, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropApplying, setCropApplying] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedFile, setCroppedFile] = useState(null);
  const [croppedPreview, setCroppedPreview] = useState(null);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const beforeUpload = (file) => {
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
    if (!ALLOWED_TYPES.includes(file.type)) {
      notification.error({ message: 'गलत फाइल प्रकार', description: 'केवल JPG, PNG या WEBP फोटो अपलोड करें', placement: 'topRight' });
      return Upload.LIST_IGNORE;
    }
    if (file.size > 5 * 1024 * 1024) {
      notification.error({ message: 'फाइल बहुत बड़ी है', description: 'फोटो का आकार 5MB से कम होना चाहिए', placement: 'topRight' });
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropModalOpen(true);
    };
    return false;
  };

  const applyCrop = async () => {
    if (!croppedAreaPixels) return;
    try {
      setCropApplying(true);
      const blob = await getCroppedBlob(rawImageSrc, croppedAreaPixels);
      if (blob.size > 2 * 1024 * 1024) {
        notification.error({ message: 'Crop के बाद भी फाइल बड़ी है', description: 'कृपया छोटा हिस्सा चुनें या दूसरी फोटो लगाएं', placement: 'topRight' });
        return;
      }
      const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
      setCroppedFile(file);
      setCroppedPreview(URL.createObjectURL(blob));
      setCropModalOpen(false);
    } catch (e) {
      notification.error({ message: 'Crop विफल', description: e.message, placement: 'topRight' });
    } finally {
      setCropApplying(false);
    }
  };

  const removePhoto = () => {
    setCroppedFile(null);
    setCroppedPreview(null);
    setRawImageSrc(null);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('name', values.name.trim());
      if (values.relation) formData.append('relation', values.relation);
      if (values.father_name?.trim()) formData.append('father_name', values.father_name.trim());
      if (values.mother_name?.trim()) formData.append('mother_name', values.mother_name.trim());
      formData.append('phone_no', values.phone_no.trim());
      formData.append('dob', values.dob.format('YYYY-MM-DD'));
      if (values.village?.trim()) formData.append('village', values.village.trim());
      if (values.district?.trim()) formData.append('district', values.district.trim());
      if (croppedFile) formData.append('profile_img', croppedFile);

      const response = await axios.post(`${baseUrl}/external-form/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.status) {
        if (drawerMode && typeof onSuccess === 'function') {
          // In drawer mode: show notification, reset form, close drawer
          notification.success({
            message: 'पंजीकरण सफल! 🎉',
            description: 'जन्मदिन पर WhatsApp पर Birthday Poster भेजा जाएगा।',
            placement: 'topRight',
          });
          form.resetFields();
          removePhoto();
          onSuccess();
        } else {
          setSubmitted(true);
        }
      } else {
        notification.error({ message: 'Error', description: response.data.message, placement: 'topRight' });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'कुछ गलत हुआ, पुनः प्रयास करें',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Shared form fields ─────────────────────────────────────────────────────
  const formFields = (
    <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
      {/* Header (shown in both modes) */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ width: 56, height: 56, background: '#7a0000', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
          <UserOutlined style={{ fontSize: 26, color: '#faad14' }} />
        </div>
        <Title level={4} style={{ margin: 0, color: '#7a0000', fontFamily: "'Noto Sans Devanagari', sans-serif" }}>
          जन्मदिन पोस्टर पंजीकरण
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          अपना विवरण हिंदी में भरें और WhatsApp पर जन्मदिन पोस्टर प्राप्त करें
        </Text>
      </div>

      <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, padding: '7px 12px', marginBottom: 16, fontSize: 12, color: '#7c5c00' }}>
        <strong>निर्देश:</strong> सभी नाम हिंदी (देवनागरी) में भरें। मोबाइल नंबर अंग्रेजी अंकों में भरें।
      </div>

      <Form.Item
        name="name"
        label={<span style={{ fontWeight: 600, fontFamily: "'Noto Sans Devanagari', sans-serif" }}>नाम <span style={{ color: 'red' }}>*</span></span>}
        rules={[{ required: true, message: 'नाम आवश्यक है' }, { min: 2 }, { max: 100 }, { validator: validateHindi }]}
      >
        <Input size="large" placeholder="आपका पूरा नाम हिंदी में लिखें" style={{ fontFamily: "'Noto Sans Devanagari', sans-serif", fontSize: 15 }} lang="hi" />
      </Form.Item>

      <Form.Item
        name="relation"
        label={<span style={{ fontWeight: 600, fontFamily: "'Noto Sans Devanagari', sans-serif" }}>संबंध (Relation)</span>}
      >
        <Select size="large" placeholder="संबंध चुनें (वैकल्पिक)" allowClear style={{ fontFamily: "'Noto Sans Devanagari', sans-serif", fontSize: 15 }}>
          <Select.Option value="सुपुत्र श्री">सुपुत्र श्री (Son of Mr.)</Select.Option>
          <Select.Option value="सुपुत्री श्री">सुपुत्री श्री (Daughter of Mr.)</Select.Option>
          <Select.Option value="पत्नी श्री">पत्नी श्री (Wife of Mr.)</Select.Option>
          <Select.Option value="गोद पुत्र श्री">गोद पुत्र श्री (Adopted Son of Mr.)</Select.Option>
          <Select.Option value="शिष्य श्री">शिष्य श्री (Disciple of Mr.)</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="father_name"
        label={<span style={{ fontWeight: 600, fontFamily: "'Noto Sans Devanagari', sans-serif" }}>पिता का नाम</span>}
        rules={[{ max: 100 }, { validator: validateHindi }]}
      >
        <Input size="large" placeholder="पिता का नाम हिंदी में (वैकल्पिक)" style={{ fontFamily: "'Noto Sans Devanagari', sans-serif", fontSize: 15 }} lang="hi" />
      </Form.Item>

      <Form.Item
        name="mother_name"
        label={<span style={{ fontWeight: 600, fontFamily: "'Noto Sans Devanagari', sans-serif" }}>माता का नाम</span>}
        rules={[{ max: 100 }, { validator: validateHindi }]}
      >
        <Input size="large" placeholder="माता का नाम हिंदी में (वैकल्पिक)" style={{ fontFamily: "'Noto Sans Devanagari', sans-serif", fontSize: 15 }} lang="hi" />
      </Form.Item>

      <Form.Item
        name="phone_no"
        label={<span style={{ fontWeight: 600 }}>WhatsApp / मोबाइल नंबर <span style={{ color: 'red' }}>*</span></span>}
        rules={[{ required: true, message: 'मोबाइल नंबर आवश्यक है' }, { pattern: /^[6-9]\d{9}$/, message: '10 अंकों का सही मोबाइल नंबर दर्ज करें' }, { validator: validateNoLinks }]}
      >
        <Input size="large" placeholder="10 अंकों का मोबाइल नंबर" maxLength={10} type="tel" inputMode="numeric" />
      </Form.Item>

      <Form.Item
        name="dob"
        label={<span style={{ fontWeight: 600 }}>जन्म तिथि <span style={{ color: 'red' }}>*</span> <span style={{ color: '#999', fontSize: 12, fontWeight: 400 }}>(दिन/महीना/वर्ष)</span></span>}
        rules={[{ required: true, message: 'जन्म तिथि आवश्यक है' }]}
      >
        <DatePicker size="large" style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="दिन/महीना/वर्ष" disabledDate={(d) => d && d.isAfter(new Date())} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="village"
            label={<span style={{ fontWeight: 600, fontFamily: "'Noto Sans Devanagari', sans-serif" }}>गाँव</span>}
            rules={[{ max: 100 }, { validator: validateHindi }]}
          >
            <Input size="large" placeholder="गाँव का नाम" style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }} lang="hi" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="district"
            label={<span style={{ fontWeight: 600, fontFamily: "'Noto Sans Devanagari', sans-serif" }}>जिला</span>}
            rules={[{ max: 100 }, { validator: validateHindi }]}
          >
            <Input size="large" placeholder="जिले का नाम" style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }} lang="hi" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label={<span style={{ fontWeight: 600 }}>फोटो <span style={{ color: '#999', fontSize: 12, fontWeight: 400 }}>(वैकल्पिक — JPG/PNG/WEBP, max 5MB)</span></span>}>
        {croppedPreview ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8 }}>
            <img src={croppedPreview} alt="preview" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '2px solid #52c41a', cursor: 'pointer' }} onClick={() => setCropModalOpen(true)} />
            <div>
              <div style={{ fontSize: 13, color: '#52c41a', fontWeight: 600 }}>✓ फोटो तैयार है</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>फोटो पर क्लिक करके फिर से Crop करें</div>
              <Button size="small" danger onClick={removePhoto} style={{ marginTop: 6 }}>फोटो हटाएं</Button>
            </div>
          </div>
        ) : (
          <Upload showUploadList={false} beforeUpload={beforeUpload} accept=".jpg,.jpeg,.png,.webp">
            <Button icon={<UploadOutlined />} size="large" style={{ width: '100%' }}>फोटो चुनें (चुनने के बाद Crop करें)</Button>
          </Upload>
        )}
      </Form.Item>

      <Button
        type="primary"
        htmlType="submit"
        loading={loading}
        size="large"
        block
        style={{ background: '#7a0000', borderColor: '#7a0000', height: 50, fontSize: 16, fontWeight: 700, marginTop: 8, fontFamily: "'Noto Sans Devanagari', sans-serif" }}
      >
        पंजीकरण करें
      </Button>
    </Form>
  );

  // ── Crop Modal (shared) ────────────────────────────────────────────────────
  const cropModal = (
    <Modal
      open={cropModalOpen}
      title={<span style={{ color: '#7a0000', fontWeight: 700 }}>फोटो Crop करें (1:1 चौकोर)</span>}
      onCancel={() => { setCropModalOpen(false); if (!croppedFile) setRawImageSrc(null); }}
      footer={[
        <Button key="cancel" onClick={() => { setCropModalOpen(false); if (!croppedFile) setRawImageSrc(null); }}>रद्द करें</Button>,
        <Button key="crop" type="primary" icon={<ScissorOutlined />} loading={cropApplying} onClick={applyCrop} style={{ background: '#7a0000', borderColor: '#7a0000' }}>Crop करके सहेजें</Button>,
      ]}
      width={520}
      destroyOnClose
    >
      <div style={{ position: 'relative', width: '100%', height: 320, background: '#111', borderRadius: 8, overflow: 'hidden' }}>
        {rawImageSrc && (
          <Cropper image={rawImageSrc} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
        )}
      </div>
      <div style={{ marginTop: 16, padding: '0 4px' }}>
        <Text style={{ fontSize: 13 }}>Zoom (बड़ा/छोटा करें):</Text>
        <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom} tooltip={{ formatter: (v) => `${v.toFixed(1)}x` }} />
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        फोटो को खींचकर सही स्थिति पर लाएं, Zoom से बड़ा/छोटा करें, फिर "Crop करके सहेजें" दबाएं।
      </Text>
    </Modal>
  );

  // ── Drawer mode: render without page wrapper ───────────────────────────────
  if (drawerMode) {
    return (
      <>
        {formFields}
        {cropModal}
      </>
    );
  }

  // ── Standalone public page ─────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <Card style={{ maxWidth: 480, width: '100%', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}>
          <Result
            status="success"
            title="पंजीकरण सफल! 🎉"
            subTitle="आपका पंजीकरण हो गया है। आपके जन्मदिन पर WhatsApp पर Birthday Poster भेजा जाएगा।"
          />
        </Card>
      </div>
    );
  }

  return (
    <>
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #7a0000 0%, #2d4a7a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <Card style={{ maxWidth: 560, width: '100%', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.22)' }} bodyStyle={{ padding: '32px 28px' }}>
          {formFields}
        </Card>
      </div>
      {cropModal}
    </>
  );
}

export default BirthdayForm;
